"use client";

import Image from "next/image";
import { Icon } from "@/components/ui/icon";
import { useConnectorLogo } from "@/features/jobs/hooks/use-connector-logo";
import type { JobAtsLinkSummary } from "@/lib/api/model";

/**
 * Pill showing a job's external ATS — connector logo + name (e.g. Ashby), or a
 * muted "No External ATS" when the job isn't linked. Shared across the jobs
 * list, the job detail header, and the candidate profile origin field.
 *
 * Pass `href` (the candidate's deep link in the ATS UI) to render the pill as
 * a link that opens the ATS profile in a new tab.
 */
export function AtsBadge({
  link,
  href,
}: {
  link?: JobAtsLinkSummary | null;
  href?: string | null;
}) {
  const logoSrc = useConnectorLogo(link?.connector_id);
  if (!link) {
    return (
      <div className="bg-contrast/4 border border-contrast/5 inline-flex items-center justify-center rounded-md p-0.5">
        <div className="flex items-center px-0.5">
          <Icon
            name="IconSquareArrowTopRight2"
            size={13}
            className="text-contrast/50"
          />
        </div>
        <div className="flex items-center pl-0.5 pr-0.5">
          <span className="text-xs font-medium text-contrast/50 leading-4 whitespace-nowrap">
            No External ATS
          </span>
        </div>
      </div>
    );
  }

  const body = (
    <>
      <div className="flex items-center px-0.5">
        {logoSrc ? (
          <Image
            src={logoSrc}
            alt=""
            width={14}
            height={14}
            className="size-3.5 rounded-[3px] object-cover shrink-0"
          />
        ) : (
          <Icon
            name="IconSquareArrowTopRight2"
            size={13}
            className="text-contrast/80"
          />
        )}
      </div>
      <div className="flex items-center pl-0.5 pr-0.5">
        <span className="text-xs font-medium text-contrast/80 leading-4 whitespace-nowrap">
          {link.connector_name}
        </span>
      </div>
      {href && (
        <div className="flex items-center pr-0.5">
          <Icon
            name="IconSquareArrowTopRight2"
            size={11}
            className="text-contrast/40"
          />
        </div>
      )}
    </>
  );

  if (href) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        title={`Open in ${link.connector_name}`}
        className="bg-contrast/4 border border-contrast/5 inline-flex items-center justify-center rounded-md p-0.5 transition-colors hover:bg-contrast/8 cursor-pointer"
      >
        {body}
      </a>
    );
  }

  return (
    <div className="bg-contrast/4 border border-contrast/5 inline-flex items-center justify-center rounded-md p-0.5">
      {body}
    </div>
  );
}
